import React, { useState, useEffect, useRef } from 'react'
import {
  Icon, Popup, List, Button, Header, Segment, Label, Transition,
  Loader, Placeholder, Dropdown, Message
} from 'semantic-ui-react'
import { useAppContext } from '../hooks/useAppContext'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { useHistory } from 'react-router-dom'

export default function NotificationBell() {

  const { user } = useAppContext()
  const history = useHistory()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [shownPushNotifications, setShownPushNotifications] = useState(new Set())
  const intervalRef = useRef(null)
  const isInitialized = useRef(false)
  // Nuevo: animación para el badge
  const [animateBadge, setAnimateBadge] = useState(false)
  // Nuevo: filtro por tipo
  const [filterType, setFilterType] = useState('all')
  // Nuevo: mensaje de acción
  const [actionMsg, setActionMsg] = useState(null)

  // Solicitar permisos de notificación al cargar el componente
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Función para obtener notificaciones no leídas
  const fetchNotifications = async () => {
    if (!user || !user.id) return

    setLoading(true)
    try {
      // Consulta GraphQL real para obtener solo no leídas
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-ses': JSON.parse(localStorage.getItem('amber-session')).session
        },
        body: JSON.stringify({
          query: `
            query GetNotifications($limit: Int, $offset: Int, $unreadOnly: Boolean) {
              getNotifications(limit: $limit, offset: $offset, unreadOnly: $unreadOnly) {
                id
                title
                message
                entityType
                entityId
                notificationType
                isRead
                createdAt
              }
            }
          `,
          variables: {
            limit: 50,
            offset: 0,
            unreadOnly: true // Solo no leídas
          }
        })
      })

      const result = await response.json()

      if (result.errors) {
        console.error('Error en consulta GraphQL:', result.errors)
        // Fallback a datos simulados si hay error
        const mockNotifications = [
          {
            id: '1',
            title: 'Nuevo comentario en vehículo',
            message: 'Juan Pérez comentó: "El vehículo necesita mantenimiento..."',
            createdAt: new Date(Date.now() - 1000 * 60 * 30),
            isRead: false,
            entityType: 'vehicle',
            entityId: '123'
          }
        ]
        setNotifications(mockNotifications)
        setUnreadCount(mockNotifications.filter(n => !n.isRead).length)
      } else {
        const notifications = result.data.getNotifications || []
        // Filtrar duplicados por ID antes de actualizar el estado
        const uniqueNotifications = notifications.filter((notification, index, self) =>
          index === self.findIndex(n => n.id === notification.id)
        )
        setNotifications(uniqueNotifications)
        setUnreadCount(uniqueNotifications.filter(n => !n.isRead).length)
      }
    } catch (error) {
      console.error('Error al obtener notificaciones:', error)
      // Fallback a datos simulados
      setNotifications([])
      setUnreadCount(0)
    } finally {
      setLoading(false)
    }
  }

  // Cargar notificaciones al montar el componente
  useEffect(() => {
    if (user && user.id && !isInitialized.current) {
      isInitialized.current = true
      fetchNotifications()

      // Actualizar notificaciones cada 30 segundos
      intervalRef.current = setInterval(fetchNotifications, 30000)

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        isInitialized.current = false
      }
    }
  }, [user && user.id]) // Volver a la sintaxis original

  // Función para marcar notificación como leída
  const markAsRead = async (notificationId) => {
    try {
      console.log('hola')
      // Mutación GraphQL para marcar como leída
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-ses': JSON.parse(localStorage.getItem('amber-session')).session
        },
        body: JSON.stringify({
          query: `
            mutation MarkNotificationAsRead($notificationId: ID!) {
              markNotificationAsRead(notificationId: $notificationId)
            }
          `,
          variables: {
            notificationId
          }
        })
      })

      const result = await response.json()

      if (result.errors) {
        console.error('Error marcando como leída:', result.errors)
      } else {
        console.log('Marcando como leída:', notificationId)

        // Actualizar estado local
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, isRead: true } : n
          )
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (error) {
      console.error('Error al marcar como leída:', error)
    }
  }

  // Botón para marcar todas como leídas
  const markAllAsRead = async () => {
    try {
      // Mutación GraphQL para marcar todas como leídas
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-ses': JSON.parse(localStorage.getItem('amber-session')).session
        },
        body: JSON.stringify({
          query: `
            mutation MarkAllNotificationsAsRead {
              markAllNotificationsAsRead
            }
          `
        })
      })
      const result = await response.json()
      if (result.errors) {
        console.error('Error marcando todas como leídas:', result.errors)
      } else {
        // Actualizar estado local
        setNotifications([])
        setUnreadCount(0)
        setActionMsg({ type: 'success', content: 'Todas las notificaciones marcadas como leídas.' })
        setTimeout(() => setActionMsg(null), 1200)
      }
    } catch (error) {
      console.error('Error al marcar todas como leídas:', error)
    }
  }

  // Función para mostrar notificación push
  const showPushNotification = (notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification.id,
        requireInteraction: false
      })
    }
  }

  // Mostrar notificaciones push automáticamente cuando lleguen nuevas no leídas
  useEffect(() => {
    if (notifications.length > 0) {
      const newNotifications = notifications.filter(n =>
        !n.isRead && !shownPushNotifications.has(n.id)
      );

      newNotifications.forEach(n => {
        showPushNotification(n);
        setShownPushNotifications(prev => new Set([...prev, n.id]));
      });
    }
  }, [notifications, shownPushNotifications]);

  // Nuevo: animar badge cuando llegan nuevas no leídas
  useEffect(() => {
    if (unreadCount > 0) {
      setAnimateBadge(true)
      const timeout = setTimeout(() => setAnimateBadge(false), 600)
      return () => clearTimeout(timeout)
    }
  }, [unreadCount])

  // Función para navegar a la entidad relacionada
  const navigateToEntity = (notification) => {
    const { entityType, entityId } = notification
    console.log('notification', notification)
    // Marcar como leída
    markAsRead(notification.id)

    // Navegar según el tipo de entidad
    switch (entityType) {
      case 'vehicle':
        history.push(`/vehicle/${entityId}`)
        break
      case 'purchaseQuotation':
        history.push(`/purchase-quotation/${entityId}`)
        break
      default:
        history.push(`/${entityType}/${entityId}`)
      //console.log('Tipo de entidad no manejado:', entityType)
    }
  }

  // Nuevo: icono según tipo de notificación
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'comment':
        return <Icon name="comments" color="blue" />
      case 'assignment':
        return <Icon name="user plus" color="orange" />
      case 'system':
        return <Icon name="info circle" color="teal" />
      default:
        return <Icon name="bell outline" color="grey" />
    }
  }

  // Nuevo: botón para descartar individualmente
  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId))
    setUnreadCount(prev => Math.max(0, prev - 1))
    setActionMsg({ type: 'success', content: 'Notificación descartada.' })
    setTimeout(() => setActionMsg(null), 1200)
  }

  // Función para formatear la fecha
  const formatDate = (date) => {
    try {
      return formatDistanceToNow(new Date(date), {
        addSuffix: true,
        locale: es
      })
    } catch (error) {
      console.error('Error formateando fecha:', error)
      return 'hace un momento'
    }
  }

  // Nuevo: filtro de notificaciones
  const notificationTypes = [
    { key: 'all', value: 'all', text: 'Todas' },
    { key: 'comment', value: 'comment', text: 'Comentarios' },
    { key: 'assignment', value: 'assignment', text: 'Asignaciones' },
    { key: 'system', value: 'system', text: 'Sistema' }
  ]
  const filteredNotifications = filterType === 'all'
    ? notifications
    : notifications.filter(n => n.notificationType === filterType)

  const trigger = (
    <div style={{ position: 'relative', cursor: 'pointer' }} aria-label="Campana de notificaciones" tabIndex={0} role="button">
      <Icon name="bell" size="large" />
      {unreadCount > 0 && (
        <Transition animation="bounce" duration={600} visible={animateBadge}>
          <Label
            color="red"
            circular
            size="mini"
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              fontSize: '0.7em',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0',
              lineHeight: '1',
              zIndex: 2
            }}
            aria-label={`Tienes ${unreadCount} notificaciones no leídas`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Label>
        </Transition>
      )}
    </div>
  )

  const content = (
    <Segment basic style={{ boxShadow: '0 2px 16px #0001', borderRadius: 16, padding: 0, minWidth: 340, maxWidth: 400 }}>
      <div style={{ padding: '18px 20px 8px 20px', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 18 }}>Notificaciones</span>
            <div style={{ color: '#888', fontSize: 13 }}>
              Tienes {unreadCount} mensajes sin leer
            </div>
          </div>
          {unreadCount > 0 && (
            <Button
              icon
              size="tiny"
              basic
              style={{ marginLeft: 8, boxShadow: 'none', padding: 6 }}
              onClick={markAllAsRead}
              title="Marcar todas como leídas"
              aria-label="Marcar todas como leídas"
            >
              <Icon name="check" />
            </Button>
          )}
        </div>
      </div>
      {/* Filtro por tipo de notificación */}
      <div style={{ padding: '8px 20px 0 20px' }}>
        <Dropdown
          selection
          options={notificationTypes}
          value={filterType}
          onChange={(_, { value }) => setFilterType(value)}
          size="mini"
          style={{ marginBottom: 8 }}
          aria-label="Filtrar notificaciones"
        />
      </div>
      {/* Mensaje de acción */}
      {actionMsg && (
        <Message
          positive={actionMsg.type === 'success'}
          negative={actionMsg.type === 'error'}
          size="tiny"
          style={{ margin: 8 }}
          content={actionMsg.content}
        />
      )}
      {/* Loader tipo skeleton */}
      {loading ? (
        <Segment basic textAlign="center" style={{ border: 'none', boxShadow: 'none' }}>
          <Loader active inline size="small" />
          <Placeholder style={{ marginTop: 10 }}>
            <Placeholder.Header image>
              <Placeholder.Line length="medium" />
              <Placeholder.Line length="full" />
            </Placeholder.Header>
            <Placeholder.Paragraph>
              <Placeholder.Line length="short" />
            </Placeholder.Paragraph>
          </Placeholder>
        </Segment>
      ) : filteredNotifications.length === 0 ? (
        <Segment basic textAlign="center" style={{ border: 'none', boxShadow: 'none' }}>
          <Icon name="check circle" color="green" />
          No tienes notificaciones nuevas
        </Segment>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto', padding: '0 0 8px 0' }}>
          <List divided relaxed selection role="list" aria-label="Lista de notificaciones" style={{ margin: 0 }}>
            {filteredNotifications.map(notification => (
              <List.Item
                key={notification.id}
                style={{
                  opacity: notification.isRead ? 0.7 : 1,
                  background: notification.isRead ? 'transparent' : '#f8fafd',
                  border: 'none',
                  borderRadius: 10,
                  margin: '0 10px 8px 10px',
                  padding: '14px 12px',
                  boxShadow: notification.isRead ? 'none' : '0 1px 4px #0001',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  cursor: 'pointer',
                  transition: 'background 0.2s, box-shadow 0.2s'
                }}
                onClick={() => navigateToEntity(notification)}
                tabIndex={0}
                aria-label={`Notificación: ${notification.title}`}
                role="listitem"
                onKeyDown={e => { if (e.key === 'Enter') navigateToEntity(notification) }}
              >
                {/* Icono circular a la izquierda */}
                <div style={{ flex: 'none', marginRight: 8 }}>
                  <Icon
                    name={notification.notificationType === 'comment' ? 'comments' : notification.notificationType === 'assignment' ? 'user plus' : 'bell outline'}
                    circular
                    color="blue"
                    style={{ background: '#eaf1fb', color: '#2185d0', fontSize: 22 }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: '#222', marginBottom: 2 }}>
                    {notification.title}
                  </div>
                  <div style={{ color: '#666', fontSize: 14, marginBottom: 4, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                    {notification.message}
                  </div>
                  <div style={{ color: '#aaa', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="clock outline" size="small" style={{ margin: 0 }} />
                    {formatDate(notification.createdAt)}
                  </div>
                </div>
              </List.Item>
            ))}
          </List>
        </div>
      )}
    </Segment>
  )

  return (
    <Popup
      trigger={trigger}
      content={content}
      on="click"
      position="bottom right"
      open={showNotifications}
      onOpen={() => setShowNotifications(true)}
      onClose={() => setShowNotifications(false)}
      style={{ padding: 0 }}
      role="dialog"
      aria-modal="true"
      aria-label="Panel de notificaciones"
    />
  )
} 